import {
  CalendarClock,
  FileText,
  Globe,
  HelpCircle,
  Mail,
  Search,
  UserPlus,
  Users,
} from 'lucide-react-native';
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TwitterIcon,
} from '../../../components/icons/BrandIcons';
import { Box, HStack, Text } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { LeadSource } from '../../../types/leads';

const sourceConfig: Record<
  LeadSource,
  { labelKey: string; color: string; bg: string; icon: ReactNode }
> = {
  email: {
    labelKey: 'common.sources.email',
    color: colors.status.info.main,
    bg: colors.status.info.bg,
    icon: <Mail size={12} color={colors.status.info.main} strokeWidth={2} />,
  },
  linkedin: {
    labelKey: 'common.sources.linkedin',
    color: colors.brand.linkedin,
    bg: colors.brand.linkedinBg,
    icon: <LinkedinIcon size={12} color={colors.brand.linkedin} strokeWidth={2} />,
  },
  instagram: {
    labelKey: 'common.sources.instagram',
    color: colors.brand.instagram,
    bg: colors.brand.instagramBg,
    icon: <InstagramIcon size={12} color={colors.brand.instagram} strokeWidth={2} />,
  },
  facebook: {
    labelKey: 'common.sources.facebook',
    color: colors.brand.facebook,
    bg: colors.brand.facebookBg,
    icon: <FacebookIcon size={12} color={colors.brand.facebook} strokeWidth={2} />,
  },
  twitter: {
    labelKey: 'common.sources.twitter',
    color: colors.brand.twitter,
    bg: colors.brand.twitterBg,
    icon: <TwitterIcon size={12} color={colors.brand.twitter} strokeWidth={2} />,
  },
  web_form: {
    labelKey: 'common.sources.form',
    color: colors.accent.purple[500],
    bg: colors.accent.purple.bg,
    icon: <FileText size={12} color={colors.accent.purple[500]} strokeWidth={2} />,
  },
  scraping: {
    labelKey: 'common.sources.scraping',
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
    icon: <Search size={12} color={colors.status.warning.main} strokeWidth={2} />,
  },
  linkedin_enrichment: {
    labelKey: 'common.sources.linkedin',
    color: colors.brand.linkedin,
    bg: colors.brand.linkedinBg,
    icon: <LinkedinIcon size={12} color={colors.brand.linkedin} strokeWidth={2} />,
  },
  linkedin_segment_search: {
    labelKey: 'common.sources.linkedin',
    color: colors.brand.linkedin,
    bg: colors.brand.linkedinBg,
    icon: <LinkedinIcon size={12} color={colors.brand.linkedin} strokeWidth={2} />,
  },
  instagram_enrichment: {
    labelKey: 'common.sources.instagram',
    color: colors.brand.instagram,
    bg: colors.brand.instagramBg,
    icon: <InstagramIcon size={12} color={colors.brand.instagram} strokeWidth={2} />,
  },
  instagram_segment_search: {
    labelKey: 'common.sources.instagram',
    color: colors.brand.instagram,
    bg: colors.brand.instagramBg,
    icon: <InstagramIcon size={12} color={colors.brand.instagram} strokeWidth={2} />,
  },
  manual: {
    labelKey: 'common.sources.manual',
    color: colors.primary[500],
    bg: colors.primary.muted,
    icon: <UserPlus size={12} color={colors.primary[500]} strokeWidth={2} />,
  },
  website: {
    labelKey: 'common.sources.website',
    color: colors.status.success.main,
    bg: colors.status.success.bg,
    icon: <Globe size={12} color={colors.status.success.main} strokeWidth={2} />,
  },
  referral: {
    labelKey: 'common.sources.referral',
    color: colors.accent.purple[400],
    bg: colors.accent.purple.bg,
    icon: <Users size={12} color={colors.accent.purple[400]} strokeWidth={2} />,
  },
  import: {
    labelKey: 'common.sources.other',
    color: colors.text.tertiary,
    bg: colors.background.secondary,
    icon: <HelpCircle size={12} color={colors.text.tertiary} strokeWidth={2} />,
  },
  other: {
    labelKey: 'common.sources.other',
    color: colors.text.tertiary,
    bg: colors.background.secondary,
    icon: <HelpCircle size={12} color={colors.text.tertiary} strokeWidth={2} />,
  },
  event: {
    labelKey: 'common.sources.event',
    color: colors.status.info.main,
    bg: colors.status.info.bg,
    icon: <CalendarClock size={12} color={colors.status.info.main} strokeWidth={2} />,
  },
};

interface SourceBadgeProps {
  source: LeadSource;
  showLabel?: boolean;
}

export function SourceBadge({ source, showLabel = true }: Readonly<SourceBadgeProps>) {
  const { t } = useTranslation();
  const config = sourceConfig[source] || sourceConfig.other;
  const iconOnly = !showLabel;

  // Icon-only mode renders as a rounded-square icon well (matching the lead
  // detail header actions / AI insight wells): neutral subtle fill, soft
  // border, and a source-colored 16px glyph. The labeled mode stays a pill.
  const icon =
    iconOnly && isValidElement(config.icon)
      ? cloneElement(config.icon as ReactElement<{ size?: number }>, { size: 16 })
      : config.icon;

  if (iconOnly) {
    return (
      <Box
        width={36}
        height={36}
        borderRadius="md"
        borderWidth={1}
        borderColor={colors.border.muted}
        backgroundColor={colors.surface.iconWell}
        alignItems="center"
        justifyContent="center"
        accessibilityRole="image"
        accessibilityLabel={t(config.labelKey)}
        style={{ alignSelf: 'flex-start' }}
      >
        {icon}
      </Box>
    );
  }

  return (
    <Box
      backgroundColor={config.bg}
      borderRadius="full"
      paddingX="sm"
      paddingY="xxs"
      style={{ alignSelf: 'flex-start' }}
    >
      <HStack spacing="xs" align="center">
        {icon}
        {showLabel && (
          <Text variant="caption" color={config.color} style={{ fontWeight: '600', fontSize: 12 }}>
            {t(config.labelKey)}
          </Text>
        )}
      </HStack>
    </Box>
  );
}
